import {
  Prompt,
  Domain,
  Brand,
  BrandSuggestion,
} from '../types';
import {
  getAllByIndex,
  getAllFromStore,
  getDataset,
  putDataset,
  putRecord,
  type DatasetName,
  type StoredBrand,
  type StoredBrandSuggestion,
  type WorkspaceItem,
} from '../db/indexedDB';
import { type RecommendationStatus } from '../db/recommendationSeeds';

// Simulate API delay
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

class PeecAPI {
  private token = '';

  private async getRequiredDataset<T>(companyId: string, name: DatasetName): Promise<T> {
    const value = await getDataset<T>(companyId, name);
    if (value === undefined) {
      throw new Error(`Missing IndexedDB dataset: ${companyId}:${name}`);
    }

    return value;
  }

  setToken(token: string) {
    this.token = token;
  }

  // Overview
  async getOverview(companyId: string) {
    await delay(300);
    return this.getRequiredDataset(companyId, 'overview');
  }

  // Prompts
  async getPrompts(companyId: string, page: number = 1, limit: number = 10) {
    await delay(300);
    return this.getRequiredDataset(companyId, 'prompts');
  }

  async createPrompt(companyId: string, prompt: Partial<Prompt>) {
    await delay(500);
    return {
      id: `prompt_${Date.now()}`,
      ...prompt,
      created_at: new Date().toISOString(),
      used_count: 0,
    };
  }

  // Domains
  async getDomains(companyId: string, page: number = 1, limit: number = 20) {
    await delay(300);
    return this.getRequiredDataset(companyId, 'domains');
  }

  async addDomain(companyId: string, domain: Partial<Domain>) {
    await delay(500);
    return {
      id: `domain_${Date.now()}`,
      status: 'active',
      added_date: new Date().toISOString(),
      url_count: 0,
      last_crawled: new Date().toISOString(),
      health: 'good',
      ...domain,
    };
  }

  // URLs
  async getUrls(companyId: string, domainId?: string, page: number = 1, limit: number = 50) {
    await delay(300);
    return this.getRequiredDataset(companyId, 'urls');
  }

  // Gap Analysis
  async getGapAnalysis(companyId: string) {
    await delay(400);
    return this.getRequiredDataset(companyId, 'gapAnalysis');
  }

  // Insights
  async getInsights(companyId: string) {
    await delay(400);
    return this.getRequiredDataset(companyId, 'insights');
  }

  // Earned (Off-page)
  async getEarned(companyId: string) {
    await delay(400);
    return this.getRequiredDataset(companyId, 'earned');
  }

  // Owned (On-page)
  async getOwned(companyId: string) {
    await delay(400);
    return this.getRequiredDataset(companyId, 'owned');
  }

  // Impact
  async getImpact(companyId: string) {
    await delay(400);
    return this.getRequiredDataset(companyId, 'impact');
  }

  // Profile
  async getProfile(companyId: string) {
    await delay(300);
    return this.getRequiredDataset(companyId, 'profile');
  }

  async updateProfile(companyId: string, profile: Record<string, unknown>) {
    await delay(500);
    const current = await this.getProfile(companyId) as Record<string, unknown>;
    const next = { ...current, ...profile };
    await putDataset(companyId, 'profile', next);
    return next;
  }

  // Brands
  async getBrands(companyId: string, page: number = 1, limit: number = 20) {
    await delay(300);
    const [brands, suggestions] = await Promise.all([
      getAllByIndex<StoredBrand>('brands', 'workspaceId', companyId),
      getAllByIndex<StoredBrandSuggestion>('brandSuggestions', 'workspaceId', companyId),
    ]);

    return {
      data: brands.map(({ workspaceId, created_at, updated_at, ...brand }) => brand),
      suggestions: suggestions.map(({ workspaceId, created_at, updated_at, ...suggestion }) => suggestion),
      total: brands.length,
      page,
      limit,
      trial_ends_in_days: 0,
    };
  }

  async createBrand(
    companyId: string,
    brand: Pick<Brand, 'display_name' | 'tracked_names' | 'domains'>
  ): Promise<Brand> {
    await delay(300);
    const baseName = brand.tracked_names[0] ?? brand.display_name;
    const createdBrand: Brand = {
      id: `brand_${Date.now()}`,
      name: baseName,
      display_name: brand.display_name,
      tracked_names: brand.tracked_names,
      domains: brand.domains,
      mentions: 0,
      color: 'sky',
      is_active: true,
    };

    await putRecord<StoredBrand>('brands', {
      ...createdBrand,
      workspaceId: companyId,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    });

    return createdBrand;
  }

  async updateBrandSuggestion(
    companyId: string,
    suggestionId: string,
    status: BrandSuggestion['status']
  ) {
    await delay(200);
    const suggestion = await getAllByIndex<StoredBrandSuggestion>('brandSuggestions', 'workspaceId', companyId)
      .then((items) => items.find((item) => item.id === suggestionId));

    if (suggestion) {
      await putRecord<StoredBrandSuggestion>('brandSuggestions', {
        ...suggestion,
        status,
        updated_at: new Date().toISOString(),
      });
    }

    return { suggestionId, status };
  }

  async getDomainMovers(companyId: string) {
    await delay(200);
    return this.getRequiredDataset(companyId, 'domainMovers');
  }

  async getLLMPlatforms(companyId: string) {
    await delay(200);
    return this.getRequiredDataset(companyId, 'llmPlatforms');
  }

  async getBrandProfile(companyId: string) {
    await delay(200);
    return this.getRequiredDataset(companyId, 'brandProfile');
  }

  async updateBrandProfile(companyId: string, profile: Record<string, unknown>) {
    await delay(200);
    const current = await this.getBrandProfile(companyId);
    const next = { ...current, ...profile };
    await putDataset(companyId, 'brandProfile', next);
    return next;
  }

  async getEarnedRecommendations(companyId: string) {
    await delay(150);
    return this.getRequiredDataset(companyId, 'earnedRecommendations');
  }

  async updateEarnedRecommendationStatus(companyId: string, recommendationId: string, status: RecommendationStatus) {
    await delay(150);
    const current = await this.getEarnedRecommendations(companyId);
    const next = current.map((item) => item.id === recommendationId ? { ...item, status } : item);
    await putDataset(companyId, 'earnedRecommendations', next);
    return next;
  }

  async getOwnedRecommendations(companyId: string) {
    await delay(150);
    return this.getRequiredDataset(companyId, 'ownedRecommendations');
  }

  async updateOwnedRecommendationStatus(companyId: string, recommendationId: string, status: RecommendationStatus) {
    await delay(150);
    const current = await this.getOwnedRecommendations(companyId);
    const next = current.map((item) => item.id === recommendationId ? { ...item, status } : item);
    await putDataset(companyId, 'ownedRecommendations', next);
    return next;
  }

  // Workspaces
  async listWorkspaces() {
    await delay(200);
    const workspaces = await getAllFromStore<WorkspaceItem>('workspaces');
    if (workspaces.length === 0) {
      throw new Error('Missing IndexedDB workspaces');
    }

    return workspaces.map((ws) => ({
      id: ws.id,
      name: ws.name,
      domain: ws.domain,
      initials: ws.initials,
      plan: ws.plan,
    }));
  }

  /**
   * 创建新工作区 — 后端接口已预留，前端入口暂未开放
   */
  async createWorkspace(_payload: { name: string; domain: string }): Promise<never> {
    await delay(0);
    throw new Error('NOT_IMPLEMENTED: 创建工作区功能即将推出');
  }
}

export const peecAPI = new PeecAPI();
